import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useMemo } from 'react'
import type { SequentialRound } from '@clicklinx/protocol'
import { ShapeGlyph } from '../components/Shape'
import { useElapsed } from '../lib/useCountdown'
import { PlayField, fieldItemStyle } from './PlayField'

/**
 * Mode 2. A single shape flashes in and out; the player counts the flashes.
 *
 * Visibility is derived from elapsed time against the server's schedule rather
 * than from a chain of local timers. A dropped frame or a backgrounded tab then
 * costs at most one late render instead of desynchronising the whole round, and
 * a reconnecting player fast-forwards to the right place automatically.
 */
export function SequentialStage({
  round,
  startedAtLocal,
  roundKey,
}: {
  round: SequentialRound
  startedAtLocal: number
  /** Stable identity for this round: the server's own start timestamp. */
  roundKey: number
}) {
  const reduceMotion = useReducedMotion()
  /**
   * The round's timing baseline, captured once and then held.
   *
   * `startedAtLocal` derives from the clock offset, which the socket refines
   * every few seconds. Letting that change mid-round would jump the sequence
   * forwards or backwards under the player's eyes.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const roundStart = useMemo(() => startedAtLocal, [roundKey])

  const elapsed = useElapsed(roundStart, round.displayMs)

  const visible = round.items.filter(
    (item) => elapsed >= item.appearAt && elapsed < item.disappearAt,
  )

  return (
    <PlayField>
      <AnimatePresence>
        {visible.map((item) => (
          <motion.div
            key={item.id}
            className="absolute text-lime"
            style={fieldItemStyle(item)}
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.5 }}
            animate={reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.75 }}
            transition={
              reduceMotion
                ? { duration: 0.01 }
                : { type: 'spring', stiffness: 700, damping: 30, mass: 0.55 }
            }
          >
            {/* Rotation lives on an inner element: Motion owns `transform` on
                the animated wrapper and would overwrite it. */}
            <div
              className="h-full w-full"
              style={{
                transform: `rotate(${item.rotation}deg)`,
                filter: 'drop-shadow(0 0 22px rgba(215,255,62,0.35))',
              }}
            >
              <ShapeGlyph shape={item.shape} className="h-full w-full" />
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </PlayField>
  )
}
